import type * as React from "react";
import { useCallback, useState } from "react";
import { Link } from "react-router-dom";
import { Download } from "lucide-react";
import { Badge, Button, ErrorMessage, Loader } from "@/ui/atoms";
import { EmptyState, StatusBadge } from "@/ui/molecules";
import type { TAsyncState } from "@/store";
import type { TApiFileType } from "@/apis/proda_finance/types";
import { fileTypeLabel } from "./fileTypeLabel";
import type { TSubmissionHistory, TSubmissionHistoryEntry } from "./submissionHistory";

// Pinned to Europe/Berlin so a timestamp reads the same everywhere on this page —
// the Review tab's rejection banner and the header both show Berlin time (see the
// sibling `formatDateTimeEuropeBerlin` in `@/utils/europeBerlinTime`); this table
// keeps its own compact "4 Mar 2026, 11:00" style rather than that helper's format,
// but must not disagree with it about *when* something happened.
const TIMESTAMP_FORMATTER = new Intl.DateTimeFormat("en-GB", {
    timeZone: "Europe/Berlin",
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
});

const LINE_COUNT_FORMATTER = new Intl.NumberFormat("en-GB");

/** Europe/Berlin time, or an em dash when there is no timestamp to show. */
function formatTimestamp(iso: string | null): string {
    if (iso === null) return "—";
    const date = new Date(iso);
    return Number.isNaN(date.getTime()) ? "—" : TIMESTAMP_FORMATTER.format(date);
}

/**
 * When this attempt reached the state it is in.
 *
 * `rejectedAt` for a rejection, `statusChangedAt` for everything else —
 * `WorkflowService.fail` never sets `rejectedAt`, so a failed attempt would
 * otherwise have no date at all.
 */
function outcomeTimestamp(entry: TSubmissionHistoryEntry): string | null {
    return entry.rejectedAt ?? entry.statusChangedAt;
}

/**
 * Who ended this attempt, in words.
 *
 * A rejected row with no rejecter was rejected by the system — a parse failure,
 * a wrong file for the deal, a failed OTB check — so it says so rather than
 * leaving the attribution blank and reading like missing data. "by PRODA"
 * matches the Review tab's rejection banner (`ValidationOutcomePanel`'s
 * "Rejected by PRODA"), which is the established wording for the same event.
 */
function outcomeAttribution(entry: TSubmissionHistoryEntry): string | null {
    if (entry.rejectedByName !== null) return `by ${entry.rejectedByName}`;
    if (entry.status === "rejected") return "by PRODA";
    return null;
}

/**
 * Whether opening this attempt would show anything.
 *
 * A superseded or manually-rejected file is a real reviewable file; a parse
 * failure ingested no rows, so a link to it would lead to an empty table.
 */
function isOpenable(entry: TSubmissionHistoryEntry): boolean {
    return entry.totalLineItems > 0;
}

export type TSubmissionHistoryTabProps = {
    history: TAsyncState<TSubmissionHistory>;
    /**
     * Whether the viewer may download a report for a row of this file type
     * (`tbgl_file_download` / `finjournal_file_download`). Evaluated per row —
     * the history endpoint applies no file-type filter, so a TB-anchored
     * history can contain FinJournal rows (and vice versa), and each is gated
     * on its own permission.
     */
    canDownload: (fileType: TApiFileType) => boolean;
    onDownloadErrorReport: (fileId: string) => Promise<void>;
    /**
     * Re-issue the load for the month on screen.
     *
     * A failed fetch would otherwise be a dead end: the view reloads when the
     * file, the month or the tab changes, none of which a user staring at an
     * error is trying to do. Same shape as the context panel's "Try again".
     */
    onRetry: () => void;
};

/**
 * Every upload attempt for the submission's month, newest first.
 *
 * One flat table across all of the period's file types rather than a section per
 * type: the tab answers "what happened to this month", and the attempts of a TB
 * and its GL sibling interleave in that story. The rows every other file listing
 * hides — failed, rejected, superseded — are the whole point, so nothing here
 * filters them.
 */
export function SubmissionHistoryTab(props: TSubmissionHistoryTabProps): React.ReactElement {
    const { history, canDownload, onDownloadErrorReport, onRetry } = props;
    const [downloadError, setDownloadError] = useState<string | null>(null);
    const [downloadingId, setDownloadingId] = useState<string | null>(null);

    const handleDownload = useCallback(
        async (fileId: string) => {
            setDownloadError(null);
            setDownloadingId(fileId);
            try {
                await onDownloadErrorReport(fileId);
            } catch (error) {
                setDownloadError(
                    error instanceof Error ? error.message : "Could not download the error report",
                );
            } finally {
                setDownloadingId(null);
            }
        },
        [onDownloadErrorReport],
    );

    // Only a first load blanks the table; a refetch keeps the rows it already has
    // so switching month does not flash an empty panel. `idle` is the frame
    // after the view opens but before the effect has fired its request — it
    // must show the spinner too, or the tab briefly claims the period is
    // empty before anything has even been asked.
    if ((history.status === "pending" || history.status === "idle") && history.data === null) {
        return (
            <div
                data-testid="submission-history-loading"
                className="flex items-center justify-center py-10"
            >
                <Loader size="lg" />
            </div>
        );
    }

    if (history.status === "rejected") {
        return (
            <div data-testid="submission-history-error">
                {/* Same shape as the month-context error panel next door, so a
                    failure here offers the same way out rather than stranding
                    the view on an error it cannot clear. */}
                <EmptyState
                    variant="error"
                    title="Could not load the submission history"
                    description={history.error?.message ?? "Something went wrong."}
                    action={{ label: "Try again", onClick: onRetry }}
                />
            </div>
        );
    }

    const entries = history.data?.entries ?? [];
    if (entries.length === 0) {
        return (
            <div data-testid="submission-history-empty">
                <EmptyState
                    title="No upload attempts for this period"
                    description="Attempts appear here as soon as a file is uploaded for this month."
                />
            </div>
        );
    }

    return (
        <div data-testid="submission-history" className="space-y-3">
            {downloadError !== null && (
                <div data-testid="submission-history-download-error">
                    <ErrorMessage variant="inline" message={downloadError} />
                </div>
            )}

            <div className="overflow-x-auto">
                <table className="w-full text-sm">
                    <caption className="sr-only">
                        Every upload attempt for this submission period, newest first
                    </caption>
                    <thead>
                        <tr className="border-b text-left text-xs uppercase text-muted-foreground">
                            <th scope="col" className="px-3 py-2 font-medium">
                                Type
                            </th>
                            <th scope="col" className="px-3 py-2 font-medium">
                                File
                            </th>
                            <th scope="col" className="px-3 py-2 font-medium">
                                Status
                            </th>
                            <th scope="col" className="px-3 py-2 font-medium">
                                Uploaded by
                            </th>
                            <th scope="col" className="px-3 py-2 font-medium">
                                Uploaded
                            </th>
                            <th scope="col" className="px-3 py-2 font-medium">
                                Outcome
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {entries.map((entry) => {
                            const attribution = outcomeAttribution(entry);
                            const at = outcomeTimestamp(entry);
                            return (
                                <tr
                                    key={entry.id}
                                    data-testid="submission-history-row"
                                    className="border-b align-top last:border-0"
                                >
                                    <td className="whitespace-nowrap px-3 py-3 font-medium">
                                        {fileTypeLabel(entry.fileType)}
                                    </td>
                                    <td className="px-3 py-3">
                                        {isOpenable(entry) ? (
                                            <Link
                                                data-testid="submission-history-file-link"
                                                to={`/files/${entry.id}`}
                                                className="break-all font-medium text-primary underline underline-offset-4"
                                            >
                                                {entry.originalFilename}
                                            </Link>
                                        ) : (
                                            <span className="break-all font-medium">
                                                {entry.originalFilename}
                                            </span>
                                        )}
                                        <span className="mt-0.5 block text-xs text-muted-foreground">
                                            {LINE_COUNT_FORMATTER.format(entry.totalLineItems)}{" "}
                                            lines
                                        </span>
                                    </td>
                                    <td className="whitespace-nowrap px-3 py-3">
                                        <div className="flex flex-wrap items-center gap-1.5">
                                            {/* `TApiFileStatus` and the store's `TFileStatus` are
                                                the same 14-member union, so this needs no cast. */}
                                            <StatusBadge status={entry.status} />
                                            {entry.isActive && (
                                                <Badge variant="outline">Current</Badge>
                                            )}
                                        </div>
                                    </td>
                                    <td
                                        data-testid="submission-history-uploader"
                                        className="px-3 py-3"
                                    >
                                        {entry.uploadedByName ?? "—"}
                                    </td>
                                    <td className="whitespace-nowrap px-3 py-3 text-muted-foreground">
                                        {formatTimestamp(entry.uploadedAt ?? entry.createdAt)}
                                    </td>
                                    <td className="px-3 py-3">
                                        {entry.rejectionReason !== null && (
                                            <p className="text-destructive">
                                                {entry.rejectionReason}
                                            </p>
                                        )}
                                        {(attribution !== null || at !== null) && (
                                            <p className="text-xs text-muted-foreground">
                                                {[
                                                    attribution,
                                                    at !== null ? formatTimestamp(at) : null,
                                                ]
                                                    .filter((part) => part !== null)
                                                    .join(" · ")}
                                            </p>
                                        )}
                                        {canDownload(entry.fileType) && entry.hasErrorReport && (
                                            <Button
                                                data-testid="submission-history-error-report"
                                                variant="outline"
                                                size="sm"
                                                className="mt-2"
                                                disabled={downloadingId === entry.id}
                                                onClick={() => void handleDownload(entry.id)}
                                            >
                                                <Download className="mr-2 h-3.5 w-3.5" />
                                                Error report
                                            </Button>
                                        )}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
